import { Note } from '../types/notes';
import {
  layeredSearch,
  buildSnippet,
  compileRegex,
  LayeredHit,
} from './search/layeredSearch';

// Re-export shared helpers so existing imports from './notesSearch' keep working.
export { buildSnippet, compileRegex };

/** A note plus its precomputed searchable plain text. */
export interface NoteSearchDoc {
  note: Note;
  title: string;
  plain: string;
}

export interface NoteSearchHit {
  note: Note;
  /** 0 = exact substring, 1 = token-prefix, 2 = fuzzy. Lower is better. */
  tier: number;
  /** Snippet around the first match, with match offsets for highlighting. */
  snippet: string;
  matchStart: number;
  matchEnd: number;
}

/**
 * Layered note search. One query → one ranked, deduped hit list:
 *   tier 0 exact substring · tier 1 token-prefix · tier 2 fuzzy (typo)
 * Exact matches sort to the top; title matches outrank body matches within a
 * tier. Each note appears once, at its best tier. Delegates the tiering to the
 * shared `layeredSearch` engine.
 */
export function searchNotes(
  query: string,
  docs: NoteSearchDoc[],
): NoteSearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const hits = layeredSearch<NoteSearchDoc>({
    query,
    docs,
    fieldsOf: (d) => [
      { name: 'title', text: d.title },
      { name: 'plain', text: d.plain },
    ],
    keyOf: (d) => d.note.note_id,
  });

  const mapped = hits.map((h: LayeredHit<NoteSearchDoc>) => {
    const titleHit = h.fieldName === 'title';
    // Fuzzy tier has no locatable span; mirror the previous behavior of
    // snippeting from the start of the source text (matchIndex 0, length 0).
    const matchIndex = h.matchIndex >= 0 ? h.matchIndex : 0;
    const { snippet, matchStart, matchEnd } = buildSnippet(
      h.fieldText,
      matchIndex,
      h.matchLength,
    );
    return {
      note: h.doc.note,
      tier: h.tier,
      snippet: titleHit ? h.doc.title : snippet,
      matchStart,
      matchEnd,
      titleHit,
    };
  });

  return mapped.sort((a, b) => {
    if (a.tier !== b.tier) return a.tier - b.tier;
    if (a.titleHit !== b.titleHit) return a.titleHit ? -1 : 1;
    return a.note.note_title.localeCompare(b.note.note_title);
  });
}

/**
 * Regex-mode note search. Tests the compiled pattern against title + body.
 * Returns `null` if the pattern is invalid (caller shows an "invalid" hint).
 */
export function searchNotesRegex(
  pattern: string,
  docs: NoteSearchDoc[],
): NoteSearchHit[] | null {
  if (!pattern.trim()) return [];
  const re = compileRegex(pattern);
  if (!re) return null;

  const hits: NoteSearchHit[] = [];
  for (const doc of docs) {
    const titleMatch = re.exec(doc.title);
    if (titleMatch) {
      hits.push({
        note: doc.note,
        tier: 0,
        snippet: doc.title,
        matchStart: titleMatch.index,
        matchEnd: titleMatch.index + titleMatch[0].length,
      });
      continue;
    }
    // Reset lastIndex is unnecessary since regex is non-global.
    const bodyMatch = re.exec(doc.plain);
    if (bodyMatch) {
      const { snippet, matchStart, matchEnd } = buildSnippet(
        doc.plain,
        bodyMatch.index,
        bodyMatch[0].length,
      );
      hits.push({
        note: doc.note,
        tier: 1,
        snippet,
        matchStart,
        matchEnd,
      });
    }
  }
  return hits.sort((a, b) =>
    a.note.note_title.localeCompare(b.note.note_title),
  );
}
